// Client-safe types for provider rate sheets (broadcast rates): a provider
// provides text, documents, images or spreadsheets for a shared parsing flow.

import { type TransportMode } from './provider.types';
import { type CompanyStatisticsAttribution } from './company-statistics.types';

export const RATE_SHEET_INCOTERMS = [
  'EXW',
  'FCA',
  'CPT',
  'CIP',
  'DAP',
  'DPU',
  'DDP',
  'FAS',
  'FOB',
  'CFR',
  'CIF',
] as const;

export type RateSheetIncoterm = (typeof RATE_SHEET_INCOTERMS)[number];

export interface ProviderRateSheetParsingRuleSnapshot {
  providerId: string;
  rules: Array<{ id: string; revision: number; text: string }>;
}

export interface RateSheetFailureDetails {
  stage: 'sources' | 'model' | 'output' | 'edits' | 'locations' | 'persistence' | 'unknown';
  code: string;
  message: string;
  action: string;
  attempt: number;
  jobId: string;
  workflowId: string;
  validationPaths?: string[];
}

export interface ProviderRateSheetParsingMetadata {
  failure?: RateSheetFailureDetails;
  correctionOutcome?: 'changes' | 'no_changes' | 'needs_sources';
  suggestedParsingRule: string | null;
  suppliedRules: ProviderRateSheetParsingRuleSnapshot | null;
  providerIdentityConflict: boolean;
  correctionMessage?: string | null;
  /** Proposed row -> original snapshot index; null means an inserted/split-off row. */
  correctionRowOrigins?: Array<number | null>;
  locationMappingDiagnostics?: string[];
}

export interface RememberProviderRateSheetRule {
  providerId: string;
  text: string;
}

export interface ReusableProviderRateSheetRule {
  providerId: string;
  providerName: string;
  suggestedText: string | null;
}

export interface ProviderRateSheetParsingRuleResponse {
  id: string;
  providerId: string;
  text: string;
  revision: number;
  sourceKind: 'manual' | 'correction';
  sourceJobId: string | null;
  sourceRateSheetId: string | null;
  authorUserId: string | null;
  authorEmail: string | null;
  createdAt: string;
  updatedAt: string;
  revokedAt: string | null;
}

export type ProviderRateSheetStatus =
  | 'parsing'
  | 'parsed'
  | 'needs_review'
  | 'failed';

export const PROVIDER_RATE_SHEET_USAGE_SCOPES = [
  'public',
  'internal',
  'excluded',
] as const;

export type ProviderRateSheetUsageScope =
  (typeof PROVIDER_RATE_SHEET_USAGE_SCOPES)[number];

/** An attachment carried on the inbound email (the rate table image/PDF). */
export interface RateSheetHtmlPreview {
  html: string;
  warnings: string[];
}

export interface RateSheetAttachment {
  /** Set by intake only; not accepted as an attachment upload attribute. */
  sourceKind?: 'email_body';
  /** Legacy source URL. New uploads use an opaque private storage reference. */
  url?: string;
  /** Base64-encoded bytes, if delivered inline instead of by URL. */
  base64?: string;
  mimeType?: string;
  filename?: string;
  storageKey?: string;
  size?: number;
  sha256?: string;
  previewUrl?: string;
  normalizedText?: string;
  warnings?: string[];
}

export const RATE_SHEET_LOCATION_TYPES = [
  'place',
  'port',
  'inland_port',
  'airport',
  'rail_terminal',
] as const;

export interface RateSheetLocation {
  id: string;
  name: string;
  type: string;
  countryName: string | null;
  countryCode: string | null;
  code: string | null;
}

export interface RateSheetProviderMatch {
  providerId: string;
  providerName: string;
  reason: string;
}

export interface RateSheetEquipment {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  containerTypeName: string;
  containerTypeCode: string;
  containerTypeSortOrder: number;
}

export interface RateSheetCarrier {
  id: string;
  name: string;
  /** Older saved references predate carrier groups and represent individual carriers. */
  isCarrierGroup?: boolean;
  scacCode: string | null;
  smdgCode: string | null;
}

/** One priced lane parsed from the sheet: (POL → POD, container) → amount. */
export interface ProviderRateLineResponse {
  id: string;
  /** Transport mode for this offer; null when the source does not establish it. */
  transportMode: TransportMode | null;
  /** Port/place of loading (origin), as written on the sheet. */
  pol: string | null;
  /** Port/place of discharge (destination). */
  pod: string | null;
  originGeoObjectId?: string | null;
  destinationGeoObjectId?: string | null;
  originLocation?: RateSheetLocation | null;
  destinationLocation?: RateSheetLocation | null;
  /** Equipment or pricing basis as written on the sheet. */
  containerType: string | null;
  containerSizeId?: string | null;
  equipment?: RateSheetEquipment | null;
  amount: number | null;
  currency: string | null;
  /** Explicit trade term for this offer; named place/version remain in notes. */
  incoterm?: RateSheetIncoterm | null;
  /** Carrier / liner, e.g. "WHL", "YML", "HMM(SOC)". */
  carrier: string | null;
  carrierId?: string | null;
  carrierReference?: RateSheetCarrier | null;
  transitTimeDays: number | null;
  /** Estimated departure calendar dates at the origin, normalized to YYYY-MM-DD. */
  etdDates?: string[];
  /** Departure wording that could not be resolved to calendar dates. */
  etdUnresolvedText?: string | null;
  /** Read-only IANA timezone derived by the server from the resolved origin. */
  etdTimeZone?: string | null;
  /** Start of validity, in ISO form when resolved; otherwise original wording. */
  validFrom?: string | null;
  /** End of validity, in ISO form when resolved; otherwise original wording. */
  validUntil: string | null;
  notes: string | null;
}

export interface ProviderRateSheetResponse {
  id: string;
  providerId: string | null;
  providerName: string | null;
  linkedProviderName?: string | null;
  fromName: string | null;
  fromEmail: string | null;
  subject: string | null;
  channel: string;
  rawMessage: string;
  attachments: RateSheetAttachment[] | null;
  status: ProviderRateSheetStatus;
  usageScope: ProviderRateSheetUsageScope;
  usageComment: string | null;
  parseNotes: string | null;
  createdAt: string;
  updatedAt: string;
  lines: ProviderRateLineResponse[];
  reviewMode?: RateSheetReviewMode;
  revision?: number;
  validationErrors?: string[];
  draft?: RateSheetDraft | null;
  approvedAt?: string | null;
  approvalMode?: string | null;
  processingState?: 'queued' | 'running' | 'idle' | 'failed';
  job?: RateSheetJobResponse | null;
  proposal?: {
    id: string;
    draft: RateSheetDraft;
    baseRevision: number;
    rowOrigins?: Array<number | null>;
    reusableRule: ReusableProviderRateSheetRule | null;
  } | null;
}

export interface ProviderRateSheetListResponse {
  providerNames: string[];
  data: ProviderRateSheetResponse[];
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CurrentProviderRateLineResponse
  extends ProviderRateLineResponse {
  rateSheetId: string;
  providerId: string | null;
  providerName: string | null;
  providerLinked: boolean;
  subject: string | null;
  receivedAt: string;
  approvedAt: string;
  sheetNotes: string | null;
  validityStatus: 'valid' | 'unclear' | 'expired';
  startsInFuture: boolean;
}

export interface CurrentProviderRatesFilterOptions {
  providers: { id: string; companyName: string }[];
  origins: { value: string; label: string }[];
  destinations: { value: string; label: string }[];
  equipment: { value: string; label: string }[];
  carriers: { value: string; label: string }[];
  currencies: string[];
}

export interface CurrentProviderRatesListResponse {
  filterOptions: CurrentProviderRatesFilterOptions;
  data: CurrentProviderRateLineResponse[];
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PublicProviderRateEndpoint {
  name: string;
  code: string | null;
  type: string | null;
}

export interface PublicProviderRateLineResponse {
  transportMode: TransportMode | null;
  origin: PublicProviderRateEndpoint;
  destination: PublicProviderRateEndpoint;
  equipmentCode: string;
  equipmentName: string | null;
  isCarrierGroup: boolean;
  carrierCode: string | null;
  carrierName: string | null;
  amount: number;
  currency: string;
  incoterm: RateSheetIncoterm | null;
  transitTimeDays: number | null;
  /** Estimated departure calendar dates at the origin, normalized to YYYY-MM-DD. */
  etdDates?: string[];
  /** Departure wording that could not be resolved to calendar dates. */
  etdUnresolvedText?: string | null;
  validFrom: string | null;
  validUntil: string | null;
  startsInFuture: boolean;
}

export interface PublicProviderRatesFilterOptions {
  origins: { value: string; label: string }[];
  destinations: { value: string; label: string }[];
  equipment: { value: string; label: string }[];
}

export interface PublicProviderRatesResponse {
  statistics?: {
    profile?: CompanyStatisticsAttribution;
    provider_rates?: CompanyStatisticsAttribution;
  };
  filterOptions: PublicProviderRatesFilterOptions;
  data: PublicProviderRateLineResponse[];
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PublicRouteRateProvider {
  id: string;
  companyName: string;
  slug: string;
  logoUrl: string | null;
  isLight: boolean;
}

export interface PublicRouteRateLineResponse
  extends PublicProviderRateLineResponse {
  statistics?: CompanyStatisticsAttribution;
  provider: PublicRouteRateProvider;
}

export interface PublicRouteRatesFilterOptions
  extends PublicProviderRatesFilterOptions {
  providers: Array<{
    value: string;
    label: string;
  }>;
}

export interface PublicRouteRatesResponse {
  filterOptions: PublicRouteRatesFilterOptions;
  data: PublicRouteRateLineResponse[];
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PublicHubRateRoute {
  id: string;
  originName: string;
  destinationName: string;
  href: string;
  rateCount: number;
  providerCount: number;
}

export interface PublicHubRateProvider {
  statistics?: CompanyStatisticsAttribution;
  provider: PublicRouteRateProvider;
  rateCount: number;
  routeCount: number;
  rates: PublicProviderRateLineResponse[];
}

export interface PublicHubRateHighlightsResponse {
  routes: PublicHubRateRoute[];
  providers: PublicHubRateProvider[];
}

export type RateSheetReviewMode = 'required' | 'automatic';

export interface RateSheetDraftLine
  extends Omit<ProviderRateLineResponse, 'id'> {
  sourceReference?: string | null;
}

export interface RateSheetDraft {
  providerMatch?: RateSheetProviderMatch | null;
  providerName: string | null;
  notes: string | null;
  lines: RateSheetDraftLine[];
  warnings: string[];
}

export interface RateSheetJobResponse {
  id: string;
  kind: 'extract' | 'correct';
  state: string;
  attempts: number;
  error: string | null;
  message?: string | null;
  canRetry?: boolean;
  retryUnavailableReason?: string | null;
  instruction?: string | null;
  reviewOriginalSources?: boolean;
  failure?: RateSheetFailureDetails | null;
}
